// src/components/resources/calculators/CreativeTestingCalculator.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '~/lib/utils'
import RiMailLine from '~/icons/remix/generated/RiMailLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiMoneyDollarCircleLine from '~/icons/remix/generated/RiMoneyDollarCircleLine'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import RiBarChartLine from '~/icons/remix/generated/RiBarChartLine'
import RiTestTubeLine from '~/icons/remix/generated/RiTestTubeLine'
import RiPercentLine from '~/icons/remix/generated/RiPercentLine'
import RiPieChartLine from '~/icons/remix/generated/RiPieChartLine'
import RiLineChartLine from '~/icons/remix/generated/RiLineChartLine'
import RiSettings4Line from '~/icons/remix/generated/RiSettings4Line'
import RiRocketLine from '~/icons/remix/generated/RiRocketLine'
import { EmbeddedEmailCapture } from '~/components/forms'
import { FAQ } from '~/components/shared/FAQ'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import { calculateTestingBudget, type CalculatorInputs, type CalculatorResults } from './CreativeTestingCalculator.logic'

interface TooltipInfo {
  title: string
  description: string
  tips?: string[]
}

const TOOLTIPS: Record<string, TooltipInfo> = {
  variations: {
    title: 'Number of Creative Variations',
    description: 'The total number of different ad creatives you want to test against each other.',
    tips: [
      'Start with 3-5 variations for most tests',
      'More variations require larger budgets',
      'Test similar concepts in groups'
    ]
  },
  actionRate: {
    title: 'Action Rate per Impression',
    description: 'The percentage of impressions that result in your desired action (click, lead, purchase, etc.).',
    tips: [
      'This is based on impressions, not clicks',
      'For purchases, 0.1% means 1 purchase per 1,000 impressions',
      'Use your historical data when possible'
    ]
  },
  detectability: {
    title: 'Minimum Detectable Effect',
    description: 'The smallest percentage improvement you want to be able to detect with confidence.',
    tips: [
      'Smaller improvements need larger sample sizes',
      'Consider starting with 25-50% for creative tests',
      'Balance precision with budget constraints'
    ]
  },
  significance: {
    title: 'Statistical Significance',
    description: 'How confident you want to be that any observed differences are real and not due to chance.',
    tips: [
      '90% is recommended for most tests',
      'Higher confidence requires larger samples',
      'Consider 80% for exploratory tests'
    ]
  }
}

const GOAL_TYPES = [
  { 
    id: 'engagement', 
    label: 'Engagement', 
    description: 'CTR, clicks, video views',
    typical: '1-5%',
    defaultRate: 2.5,
    confidence: 'Highest' 
  },
  { 
    id: 'leads', 
    label: 'Leads', 
    description: 'Form fills, sign-ups',
    typical: '0.1-1%',
    defaultRate: 0.5,
    confidence: 'Medium' 
  },
  { 
    id: 'purchases', 
    label: 'Purchases', 
    description: 'Sales, transactions',
    typical: '0.01-0.5%',
    defaultRate: 0.1,
    confidence: 'Lowest' 
  }
] as const

const faqItems = [
  {
    question: "What is the Creative Testing Budget Calculator, and how does it work?",
    answer: (
      <div className="space-y-2">
        <p>The Creative Testing Budget Calculator helps advertisers determine the optimal budget and sample size needed to test different ad creatives effectively. By inputting key variables such as the number of creative variations, expected action rates, CPM, statistical significance, and minimum detectable effect (MDE), the calculator estimates:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Total required budget</li>
          <li>Per-variation budget</li>
          <li>Required impressions and conversion events</li>
          <li>Estimated timeframe for testing</li>
        </ul>
        <p>This ensures your creative testing campaign is data-driven and statistically valid, minimizing wasted ad spend while maximizing insights.</p>
      </div>
    ),
    category: "Basics",
    icon: <RiCalculatorLine />
  },
  {
    question: "How is the required budget for creative testing calculated?",
    answer: (
      <div className="space-y-2">
        <p>The budget is determined based on several critical factors:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li><strong>Number of variations:</strong> More variations require a larger budget to ensure meaningful comparisons.</li>
          <li><strong>Expected action rate (%):</strong> Likelihood of users taking desired actions (clicks, sign-ups, purchases, etc.).</li>
          <li><strong>CPM: (cost per 1,000 impressions)</strong>: Determines the cost of reaching the required audience.</li>
          <li><strong>Minimum detectable effect (MDE):</strong> Smallest improvement you want to confidently identify.</li>
          <li><strong>Statistical significance:</strong> Probability that results are reliable and not due to chance.</li>
        </ul>
        <p>By balancing these inputs, the calculator provides an optimized budget recommendation that ensures accurate and actionable test results.</p>
      </div>
    ),
    category: "Calculations",
    icon: <RiBarChartLine />
  },
  {
    question: "What is 'Minimum Detectable Effect' (MDE), and why is it important?",
    answer: (
      <div className="space-y-2">
        <p>MDE is the smallest improvement in performance that your test needs to detect to be considered statistically significant.</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Lower MDE (e.g., 10%) requires larger budget but catches small improvements</li>
          <li>Higher MDE (e.g., 50%) requires less budget but may miss incremental gains</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Optimization Tip: If your budget is limited, consider raising the MDE threshold to focus on detecting more substantial improvements.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Calculations",
    icon: <RiPercentLine />
  },
  {
    question: "Why does the required budget vary so much between different testing goals?",
    answer: (
      <div className="space-y-2">
        <p>Different actions have vastly different baseline rates:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li><strong>Engagement actions (CTR, clicks, video views):</strong> Typical rates range from 1% to 5%, meaning they occur frequently and require a lower budget.</li>
          <li><strong>Lead actions (form fills, sign-ups):</strong> Typical rates range from 0.1% to 1%, requiring a larger budget due to lower frequency.</li>
          <li><strong>Purchase actions (sales, transactions):</strong> Typical rates range from 0.01% to 0.5%, meaning they occur (relatively) infrequently and require the highest budget.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Budget-Saving Strategy: Start with an engagement test (clicks, views, etc.) to identify top performing creatives, then run a smaller purchase test with the best variations.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Budgeting",
    icon: <RiMoneyDollarCircleLine />
  },
  {
    question: "How does statistical significance affect my budget?",
    answer: (
      <div className="space-y-2">
        <p>Statistical significance (e.g., 70%, 80%, or 90% confidence level) determines test result reliability, or how certain you want to be that the test results are not due to random chance:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li><strong>70% confidence:</strong> Lower budget, but more risk of false positives</li>
          <li><strong>80% confidence:</strong> Balanced approach, good balance between budget efficiency and reliability</li>
          <li><strong>90% confidence:</strong> Higher budget, but more reliable results</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Optimization Tip: If you need to reduce costs, lowering the confidence level from 90% to 80% can significantly cut required impressions while maintaining strong test validity.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Calculations",
    icon: <RiPieChartLine />
  },
  {
    question: "How long should I run my creative testing campaign?",
    answer: (
      <div className="space-y-2">
        <p>A well-structured test should run for at least 2-3 weeks to:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Account for day-of-week effects (e.g., weekends vs. weekdays perform differently)</li>
          <li>Allow the platform ad-serving algorithms to stabilize (avoid premature optimization)</li>
          <li>Ensure sufficient data collection for meaningful results</li>
        </ul>
        <div className="mt-3 rounded-lg bg-yellow-50 p-3 dark:bg-yellow-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <p className="ml-2 text-yellow-800 dark:text-yellow-200">
              Warning: Ending a test too early (before reaching required event counts) can lead to misleading conclusions and wasted ad spend.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Implementation",
    icon: <RiTimeLine />
  },
  {
    question: "How many impressions and conversion events do I need per variation?",
    answer: (
      <div className="space-y-2">
        <p>The calculator determines this based on your expected action rate and statistical significance requirements. As a rule of thumb:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Aim for 200+ events per variation before drawing conclusions</li>
          <li>Ensure a balanced distribution of impressions across variations to avoid biased results.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-yellow-50 p-3 dark:bg-yellow-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <p className="ml-2 text-yellow-800 dark:text-yellow-200">
              Common Mistake to Avoid: If one variation gets significantly more impressions than others due to algorithmic bias, the results may not be valid.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Implementation",
    icon: <RiLineChartLine />
  },
  {
    question: "Can I reduce my budget without compromising test quality?",
    answer: (
      <div className="space-y-2">
        <p>Yes! Here are three proven ways to reduce costs while maintaining test validity:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li><strong>Increase MDE</strong>: Detect larger performance differences instead of small incremental changes.</li>
          <li><strong>Lower confidence level</strong>: Dropping from 90% to 80% reduces required impressions while still maintaining reliability.</li>
          <li><strong>Use engagement tests first</strong>: Identify top-performing creatives with cheaper engagement metrics before running costly conversion tests.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Budget-Smart Strategy: Start with a CTR or video view test to narrow down the best-performing ads before spending on conversion testing.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Budgeting",
    icon: <RiMoneyDollarCircleLine />
  },
  {
    question: "Why should I keep targeting and bidding strategies constant during the test?",
    answer: (
      <div className="space-y-2">
        <p>For a test to be scientifically valid, only the creative should change. If you adjust targeting, bidding, or audience segments mid-test:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>The test results may reflect <strong>audience differences</strong>, not creative performance.</li>
          <li>CPM fluctuations can skew <strong>cost-per-action metrics</strong>.</li>
          <li>The platform&apos;s algorithm may shift budget unevenly between variations.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Best Practice: Set up a controlled test environment by ensuring all variations receive an equal share of impressions under the same conditions.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Implementation",
    icon: <RiSettings4Line />
  },
  {
    question: "How do I interpret results and decide which creative won?",
    answer: (
      <div className="space-y-2">
        <p>Once the test concludes:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Compare key metrics (CTR, conversion rate, CPA, etc.) across variations.</li>
          <li>Ensure the winning creative reached at least 200+ events for statistical reliability.</li>
          <li>If differences are statistically significant, consider rolling out the top performer.</li>
          <li>If results are inconclusive, you may need a larger sample size or longer duration.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-yellow-50 p-3 dark:bg-yellow-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <p className="ml-2 text-yellow-800 dark:text-yellow-200">
              Common Pitfall to Avoid: Declaring a winner too early based on limited data—wait until the test has enough conversions to be reliable.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Analysis",
    icon: <RiTestTubeLine />
  },
  {
    question: "How do I apply these insights to future campaigns?",
    answer: (
      <div className="space-y-2">
        <p>The best-performing creatives should:</p>
        <ul className="ml-4 list-disc space-y-1">
          <li>Be scaled up with increased budget allocation.</li>
          <li>Serve as a blueprint for future iterations (e.g., colors, messaging, CTA placement).</li>
          <li>Be tested in new audience segments to further optimize performance.</li>
          <li>Leveraged as templates for future ad creative concepts.</li>
        </ul>
        <div className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
          <div className="flex items-center">
            <RiLightbulbLine className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <p className="ml-2 text-blue-800 dark:text-blue-200">
              Pro Tip: Once you identify a strong creative, try iterative testing—small refinements (e.g., different CTA buttons) to further improve results.
            </p>
          </div>
        </div>
      </div>
    ),
    category: "Analysis",
    icon: <RiRocketLine />
  }
]

export function CreativeTestingCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    variations: 4,
    goalType: 'engagement',
    actionRate: 2.5,
    cpm: 15,
    detectability: 25,
    significance: 90,
    hasCustomActionRate: false
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null)
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'creative-testing-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])
  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const handleGoalTypeChange = (goalType: 'engagement' | 'leads' | 'purchases'): void => {
    const goalTypeConfig = GOAL_TYPES.find(g => g.id === goalType)
    if (!goalTypeConfig) {
      throw new Error(`Invalid goal type: ${goalType}`)
    }
    setInputs(prev => ({
      ...prev,
      goalType,
      actionRate: prev.hasCustomActionRate ? prev.actionRate : goalTypeConfig.defaultRate
    }))
    trackInteract('input_change')
  }

  const handleActionRateChange = (value: number): void => {
    setInputs(prev => ({
      ...prev,
      actionRate: value,
      hasCustomActionRate: true
    }))
    trackInteract('input_change')
  }

  const handleCalculate = (): void => {
    const newResults = calculateTestingBudget(inputs)
    setResults(newResults)
    setShowEmailCapture(true)
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'creative-testing-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' }
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'creative-testing-calculator',
      engagementMs: getTotalMs(),
      params: {
        total_budget: newResults.totalBudget,
        required_impressions: newResults.requiredImpressions
      }
    })
  }

  const emailTemplate = useMemo(() => ({
    templateAlias: "welcome",
    to: '', // Will be set by EmbeddedEmailCapture
    from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
    templateModel: {
      base: {
        logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? 'https://adsights.ai/logo-square.png',
        product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? 'AdSights',
        product_url: process.env.NEXT_PUBLIC_PRODUCT_URL ?? 'https://www.adsights.ai',
        support_email: process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? 'support@adsights.ai',
        sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? 'The AdSights Team',
        company_name: process.env.NEXT_PUBLIC_COMPANY_NAME ?? 'AdSights Inc.',
        company_address: process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702'
      },
      custom: {
        results: results,
        inputs: inputs
      }
    }
  }), [results, inputs])

  const renderTooltip = (key: string): JSX.Element | null => {
    const info = TOOLTIPS[key]
    if (!info) return null

    return (
      <div className="relative ml-2 inline-block">
        <button
          type="button"
          className="text-gray-400 hover:text-gray-500"
          aria-label={`Learn more about ${info.title}`}
          onMouseEnter={() => setActiveTooltip(key)}
          onMouseLeave={() => setActiveTooltip(null)}
        >
          <RiInformationLine className="h-5 w-5" />
        </button>
        {activeTooltip === key && (
          <div className="absolute -right-4 top-8 z-10 w-72 rounded-lg bg-white p-4 text-sm shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800">
            <p className="font-medium text-gray-900 dark:text-white">
              {info.title}
            </p>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {info.description}
            </p>
            {info.tips && (
              <ul className="mt-3 space-y-2">
                {info.tips.map((tip, i) => (
                  <li key={i} className="flex items-start text-xs text-gray-500 dark:text-gray-400">
                    <RiLightbulbLine className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-400" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Creative Testing Budget Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate the optimal budget and sample size for your creative testing campaigns
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column - Basic Inputs */}
          <div className="space-y-6">
            {/* Number of Variations */}
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Number of Variations
                </label>
                {renderTooltip('variations')}
              </div>
              <input
                type="number"
                min="2"
                max="20"
                value={inputs.variations}
                onChange={(e) => { setInputs({ ...inputs, variations: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Total number of creative variations to test
              </p>
            </div>

            {/* Goal Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Testing Goal
              </label>
              <div className="mt-2 space-y-2">
                {GOAL_TYPES.map((goal) => (
                  <div
                    key={goal.id}
                    onClick={() => handleGoalTypeChange(goal.id)}
                    className={cn(
                      "cursor-pointer rounded-lg border p-4 transition-colors",
                      inputs.goalType === goal.id
                        ? "border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/20"
                        : "border-gray-200 hover:border-indigo-200 dark:border-gray-700 dark:hover:border-indigo-800"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {goal.label}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {goal.description}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-gray-900 dark:text-white">{goal.typical}</p>
                        <p className="text-gray-500 dark:text-gray-400">
                          Typical Rate
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Advanced Inputs */}
          <div className="space-y-6">
            {/* Action Rate */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Expected Action Rate (%)
                </label>
                {renderTooltip('actionRate')}
              </div>
              <input
                type="number"
                step="0.01"
                min="0.001"
                max="100"
                value={inputs.actionRate}
                onChange={(e) => { handleActionRateChange(+e.target.value) }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Your expected conversion rate per impression
              </p>
            </div>

            {/* CPM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Average CPM ($)
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={inputs.cpm}
                onChange={(e) => { setInputs({ ...inputs, cpm: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>

            {/* Minimum Detectable Effect */}
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Minimum Detectable Effect (%)
                </label>
                {renderTooltip('detectability')}
              </div>
              <input
                type="number"
                step="1"
                min="5"
                max="100"
                value={inputs.detectability}
                onChange={(e) => { setInputs({ ...inputs, detectability: +e.target.value }); trackInteract('input_change') }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Minimum improvement you want to be able to detect
              </p>
            </div>

            {/* Statistical Significance */}
            <div>
              <div className="flex items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Statistical Significance
                </label>
                {renderTooltip('significance')}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[70, 80, 90].map((level) => (
                  <button
                    key={level}
                    onClick={() => setInputs({ ...inputs, significance: level as 70 | 80 | 90 })}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm font-medium",
                      inputs.significance === level
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100"
                    )}
                  >
                    {level}%
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            Calculate Testing Budget
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8"
          >
            {/* Results Card */}
            <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Testing Budget & Requirements
              </h3>
              
              {/* Budget and Requirements Grid */}
              <div className="mt-6 grid gap-8 sm:grid-cols-2">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Recommended Budget
                  </h4>
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
                        ${Math.ceil(results.totalBudget).toLocaleString()}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Total testing budget
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                        ${Math.ceil(results.budgetPerVariation).toLocaleString()}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Per variation
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Testing Requirements
                  </h4>
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {Math.ceil(results.requiredImpressions).toLocaleString()}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Required impressions
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {results.requiredEventsPerVariation.toLocaleString()}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Required events per variation
                      </p>
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {results.estimatedTimeframe}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Estimated timeframe
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="my-8 border-t border-gray-200 dark:border-gray-800"></div>

              {/* Testing Plan & Recommendations */}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Testing Plan & Recommendations
              </h3>
              
              {/* Primary Actions */}
              <div className="mt-6 space-y-8">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Key Requirements
                  </h4>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {results.recommendedActions
                      .filter(action => action.type === 'primary')
                      .map((action, index) => (
                        <div
                          key={index}
                          className="rounded-lg bg-indigo-50 p-4 dark:bg-indigo-900/20"
                        >
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-800">
                                {action.icon === 'time' && <RiTimeLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                                {action.icon === 'goal' && <RiLightbulbLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                                {action.icon === 'distribution' && <RiMoneyDollarCircleLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                              </span>
                            </div>
                            <p className="ml-4 text-sm text-indigo-700 dark:text-indigo-300">
                              {action.text}
                            </p>
                          </div>
                        </div>
                    ))}
                  </div>
                </div>

                {/* Budget Optimization Tips */}
                {results.recommendedActions.some(action => action.type === 'optimization') && (
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Budget Optimization Options
                    </h4>
                    <div className="mt-4 space-y-3">
                      {results.recommendedActions
                        .filter(action => action.type === 'optimization')
                        .map((action, index) => (
                          <div
                            key={index}
                            className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900/50 dark:bg-yellow-900/20"
                          >
                            <div className="flex items-center">
                              <RiLightbulbLine className="h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
                              <p className="ml-3 text-sm text-yellow-800 dark:text-yellow-200">
                                {action.text}
                              </p>
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Best Practices */}
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Best Practices
                  </h4>
                  <div className="mt-4 grid gap-4">
                    {results.recommendedActions
                      .filter(action => action.type === 'tip')
                      .map((action, index) => (
                        <div
                          key={index}
                          className="flex items-center rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
                        >
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                            <RiLightbulbLine className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                          </span>
                          <p className="ml-4 text-sm text-gray-600 dark:text-gray-400">
                            {action.text}
                          </p>
                        </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Email Capture */}
            {showEmailCapture && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-8"
              >
                <EmbeddedEmailCapture
                  preset="card"
                  content={{
                    title: {
                      text: "Get a detailed testing plan",
                      className: "text-xl font-semibold text-gray-900 dark:text-white"
                    },
                    description: {
                      text: "Enter your email to receive a comprehensive testing plan with timeline, budget allocation, and best practices.",
                      className: "text-sm text-gray-600 dark:text-gray-400"
                    },
                    button: {
                      text: "Get Plan",
                      loadingText: "Sending...",
                      icon: <RiMailLine className="h-4 w-4" />
                    },
                    messages: {
                      success: "Testing plan sent!",
                      error: "Please try again."
                    },
                    privacy: {
                      enabled: true,
                      text: "By continuing, you agree to our",
                      className: "text-xs text-gray-500 dark:text-gray-400"
                    }
                  }}
                  template={emailTemplate}
                  analytics={{
                    enabled: true,
                    category: "calculator",
                    label: "creative_testing",
                    elementId: "creative_testing_email_capture"
                  }}
                  onSuccess={() => {
                    // Optional: Add any success handling
                  }}
                  onError={(error) => {
                    logClientError(error, { component: 'CreativeTestingCalculator', context: 'Email capture error' })
                  }}
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section */}
      <div className="mt-16">
        <FAQ
          items={faqItems}
          title="Creative Testing FAQ"
          type="single"
          description="Learn more about creative testing budgets and best practices"
          disableSchema={true}
          theme={{
            variant: "integrated",
            size: "sm"
          }}
          features={{
            showTitle: true,
            showDescription: true,
            showIcons: true,
            categorized: true,
            allowMultiple: false,
            animated: true
          }}
        />
      </div>
    </div>
  )
}

export default CreativeTestingCalculator
